import { useEffect, useMemo } from 'react';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useSessionReportStore } from '../../../stores/prioritization/sessionReportStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { useSessionResultsAnalytics } from '../hooks/useSessionResultsAnalytics';
import { AlignmentSummary } from './results/AlignmentSummary';
import { ItemCriteriaRadar } from './results/ItemCriteriaRadar';
import { ParticipantAlignmentScatter } from './results/ParticipantAlignmentScatter';
import { PriorityCascade } from './results/PriorityCascade';
import { C } from '../theme/participantTokens';
import { LayerBack, LayerBar } from './layout/ParticipantLayout';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';

interface SessionResultsViewProps {
  session: PrioritizationSession;
  onBack: () => void;
}

export function SessionResultsView({ session, onBack }: SessionResultsViewProps) {
  const getItemById = useItemsStore(s => s.getItemById);
  const fetchReports = useSessionReportStore(s => s.fetchReportsForSession);
  const report = useSessionReportStore(s => s.getReportForSession(session.id));
  const criteriaIds = session.criteriaConfig?.criteriaIds ?? [];

  useEffect(() => {
    void fetchReports(session.id);
  }, [session.id, fetchReports]);

  const rankingOutput = session.rankingOutput ?? undefined;
  const reportRanking = useMemo(() => {
    const ranking = report?.outputsSnapshot?.ranking;
    if (!Array.isArray(ranking)) return undefined;
    return ranking as Array<{ itemId: string; rank: number; score?: number }>;
  }, [report]);

  const {
    loading,
    rankedItems,
    itemSpreads,
    alignment,
    participantRanksByItem,
  } = useSessionResultsAnalytics(session, rankingOutput, reportRanking);

  const isPublished = session.state === 'PUBLISHED';
  const maxScore = Math.max(...rankedItems.map(r => r.score), 1);

  const cascadeItems = rankedItems.map(r => ({
    rank: r.rank,
    title: getItemById(r.itemId)?.title ?? r.itemId,
    score: r.normalizedScore > 0 ? r.normalizedScore : r.score,
  }));

  return (
    <>
      <LayerBar>
        <LayerBack label="Sessions" onClick={onBack} />
        <div style={{ padding: '0 16px', fontFamily: 'Spectral, Georgia, serif', fontSize: '1.05rem', color: C.textSec, borderRight: `1px solid ${C.rule}` }}>
          {session.name}
        </div>
        <div style={{ marginLeft: 'auto', padding: '0 16px', fontFamily: 'JetBrains Mono', fontSize: '0.75rem', color: C.green, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, flexShrink: 0 }} />
          {isPublished ? 'Published' : 'Complete'}
        </div>
      </LayerBar>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '28px 32px 48px', maxWidth: 860 }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.78rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: C.textDim, marginBottom: 4 }}>
            {session.trackId ?? 'Session Results'}
          </div>
          <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1.9rem', fontWeight: 600, color: C.textPri, marginBottom: 4 }}>
            {session.name}
          </div>
          <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '1rem', fontWeight: 300, color: C.textTer, marginBottom: 24 }}>
            {alignment
              ? `${alignment.submittedCount} / ${alignment.totalParticipants} participants submitted`
              : `${session.participantIds?.length ?? 0} participants`}
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
            {[
              { label: 'Items ranked', value: rankedItems.length, color: C.textPri },
              {
                label: 'Submitted',
                value: alignment ? `${alignment.submittedCount}/${alignment.totalParticipants}` : '—',
                color: C.textPri,
              },
              {
                label: 'Your alignment',
                value: alignment && alignment.overallPct > 0 ? `${alignment.overallPct}%` : '—',
                color: C.green,
              },
              {
                label: 'Review status',
                value: isPublished ? 'Published' : 'Pending',
                color: isPublished ? C.green : C.mustard,
              },
            ].map(stat => (
              <div
                key={stat.label}
                className="pp-anim"
                style={{ flex: 1, minWidth: 100, background: C.surface, border: `1px solid ${C.rule}`, borderRadius: 4, padding: '14px 16px' }}
              >
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: '1.5rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
                <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.9rem', color: C.textTer, marginTop: 3 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {loading && (
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.82rem', color: C.textDim, marginBottom: 16 }}>Loading alignment data…</div>
          )}

          {cascadeItems.length > 0 && <PriorityCascade items={cascadeItems} />}

          {itemSpreads.length > 0 && (
            <ParticipantAlignmentScatter
              spreads={itemSpreads}
              participantRanksByItem={participantRanksByItem}
              getItemLabel={id => getItemById(id)?.title ?? id.slice(0, 8)}
            />
          )}

          {alignment && alignment.submittedCount > 0 && <AlignmentSummary alignment={alignment} />}

          {rankedItems.length > 0 ? (
            <>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.75rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.textDim, marginBottom: 10 }}>
                Full Ranking
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {rankedItems.map((row, idx) => {
                  const item = getItemById(row.itemId);
                  const pct = maxScore > 0 ? (row.score / maxScore) * 100 : 0;
                  const hasCriteria = Object.keys(row.criterionScores).length > 0 || criteriaIds.length > 0;

                  return (
                    <div
                      key={row.itemId}
                      className="pp-anim"
                      style={{
                        background: C.surface,
                        border: `1px solid ${C.rule}`,
                        borderRadius: 4,
                        padding: '14px 16px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: hasCriteria ? 10 : 0 }}>
                        <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.9rem', fontWeight: 700, color: idx < 3 ? C.mustard : C.textDim, width: 24, textAlign: 'right', flexShrink: 0, paddingTop: 2 }}>
                          {row.rank}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1.1rem', color: C.textPri, marginBottom: 2 }}>
                            {item?.title ?? row.itemId}
                          </div>
                          <ItemRelationshipFlags itemId={row.itemId} />
                          {item?.itemCategory && (
                            <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.75rem', letterSpacing: '0.08em', color: C.textDim }}>
                              {item.itemCategory}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 90, flexShrink: 0 }}>
                          <div style={{ flex: 1, height: 3, background: C.ruleBright, minWidth: 60 }}>
                            <div className="pp-draw-bar" style={{ height: '100%', width: `${pct}%`, background: C.mustard, opacity: 0.6 }} />
                          </div>
                          <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.85rem', fontWeight: 700, color: C.mustard, opacity: 0.8 }}>
                            {row.score.toFixed(1)}
                          </span>
                        </div>
                      </div>
                      {hasCriteria && (
                        <div style={{ marginLeft: 36 }}>
                          <ItemCriteriaRadar
                            itemId={row.itemId}
                            criterionScores={row.criterionScores}
                            criteriaIds={criteriaIds}
                            compositeScore={row.score}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{ background: C.surface, border: `1px solid ${C.rule}`, borderRadius: 4, padding: 24, textAlign: 'center', fontSize: '1rem', color: C.textTer, marginBottom: 20 }}>
              Results are being finalised by the facilitator.
            </div>
          )}

          <div style={{ background: C.surface, border: `1px solid ${C.rule}`, borderLeft: '2px solid rgba(192,83,58,0.5)', borderRadius: 4, padding: '16px 18px' }}>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.78rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(192,83,58,0.7)', marginBottom: 8 }}>
              What Happens Next
            </div>
            <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '1rem', fontWeight: 300, color: C.textTer, lineHeight: 1.6 }}>
              {isPublished
                ? 'These results are published and contribute to the track prioritization.'
                : 'These results are pending review by the session facilitator before publication.'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
